import { Link } from 'react-router';
import { COMPANY } from './company';
import { Bullets, Email, LegalPage, Term, type LegalSection } from './LegalPage';

/**
 * The privacy policy at my.stratum.ie/privacy.
 *
 * <p>
 * Section "connected-email" is the one Google's verification reads line by
 * line before approving the Gmail send permission: what Google data is
 * accessed, why, that it is not shared or used for anything else, and the
 * Limited Use statement in Google's own wording. Change it with that in mind —
 * and change the Microsoft paragraph with it, since the two must match.
 * </p>
 *
 * <p>
 * DRAFT TEXT. It describes what the platform actually does, but it has not been
 * reviewed by a lawyer; it should be before it is relied on.
 * </p>
 */
const SECTIONS: LegalSection[] = [
  {
    id: 'who-we-are',
    heading: 'Who we are',
    body: (
      <>
        <p>
          {COMPANY.product} is a software platform for transport and logistics businesses, provided
          by {COMPANY.legalName}, trading as {COMPANY.name} (“{COMPANY.name}”, “we”, “us”), a company
          registered in{' '}
          {COMPANY.jurisdiction} under number {COMPANY.companyNumber}, with its registered office at{' '}
          {COMPANY.registeredAddress}.
        </p>
        <p>
          This policy explains what personal data {COMPANY.product} processes, why, and the choices
          and rights you have. It covers the {COMPANY.product} web apps (including myTMS,
          myDepot, myCheck and mySubsistence), the myDriver mobile app, and the website at{' '}
          {COMPANY.portal}.
        </p>
      </>
    ),
  },
  {
    id: 'our-role',
    heading: 'Our role: controller and processor',
    body: (
      <>
        <p>
          {COMPANY.product} is used by businesses (“customers”). The data a customer puts into the
          platform — its orders, its own customers and suppliers, its staff, drivers and vehicles —
          belongs to that customer.
        </p>
        <Bullets
          items={[
            <Term key="p" label="As a processor">
              we handle that business data only on the customer’s instructions, to provide the
              service. The customer is the controller, and questions about it are best directed to
              the business that uses {COMPANY.product}.
            </Term>,
            <Term key="c" label="As a controller">
              we decide how to handle the data we need to run our own business: user accounts and
              sign-in, billing contacts, support conversations, and security and usage logs.
            </Term>,
          ]}
        />
      </>
    ),
  },
  {
    id: 'what-we-collect',
    heading: 'The information we process',
    body: (
      <Bullets
        items={[
          <Term key="acc" label="Account and user details">
            names, work email addresses, phone numbers, job roles, the organisations and apps a
            person has access to, and their sign-in history.
          </Term>,
          <Term key="biz" label="Business records">
            orders, jobs, rates, charges, invoices and credit notes, together with the contact
            details of the people at a customer’s own customers and suppliers.
          </Term>,
          <Term key="drv" label="Driver and fleet data">
            for drivers using myDriver: the phone number used to sign in, assigned jobs, vehicle
            checks and defects, photos and signatures captured in the app, proof-of-delivery records
            and messages with the office. Where a customer connects a GPS or tachograph provider,
            vehicle location and driving-activity data from that provider.
          </Term>,
          <Term key="mail" label="Connected email accounts">
            the email address of a connected Microsoft 365 or Google mailbox, and an encrypted access
            token — see section 4.
          </Term>,
          <Term key="tech" label="Technical data">
            IP address, browser and device type, app version, and logs of requests and errors, used
            to keep the service secure and working.
          </Term>,
        ]}
      />
    ),
  },
  {
    id: 'connected-email',
    heading: 'Connected email accounts (Microsoft 365 and Google)',
    body: (
      <>
        <p>
          An organisation can connect its own Microsoft 365 or Google Workspace mailbox so that
          documents it sends from {COMPANY.product} — such as invoices, credit notes, quotes, proofs
          of delivery and job confirmations — reach its customers from its own email address instead
          of ours. Connecting is optional; without it, we send these emails through our own email
          provider.
        </p>
        <p>
          <strong className="font-semibold text-slate-900">What we ask for.</strong> When a user
          connects a mailbox, Microsoft or Google asks them to grant {COMPANY.product}:
        </p>
        <Bullets
          items={[
            <>
              permission to <strong className="font-semibold text-slate-900">send email</strong> as
              that address (Microsoft Graph <code>Mail.Send</code> and <code>Mail.Send.Shared</code>;
              Google <code>gmail.send</code>);
            </>,
            <>their basic profile and email address, to show which mailbox is connected;</>,
            <>offline access, so documents can be sent when that user is not signed in.</>,
          ]}
        />
        <p>
          <strong className="font-semibold text-slate-900">What we do with it.</strong> We use this
          access only to send the emails a user of that organisation asks {COMPANY.product} to send,
          to the recipients shown before sending, and to send a test message when the mailbox is
          connected. Sent messages appear in that mailbox’s Sent folder, as they would from any
          email program.
        </p>
        <p>
          <strong className="font-semibold text-slate-900">What we never do.</strong> We do not read,
          search, store, delete or analyse the messages, contacts or calendar in a connected mailbox
          — the permissions we ask for do not allow it. We do not sell this data, use it for
          advertising, use it to train artificial-intelligence or machine-learning models, or let
          any person read it. We do not transfer it to anybody except as needed to send the email
          the user asked for, to comply with the law, or as part of a merger or acquisition with
          notice to you.
        </p>
        <p>
          <strong className="font-semibold text-slate-900">How it is protected.</strong> The access
          token Microsoft or Google issues is encrypted before it is stored, with a key held
          separately from the database, and is only ever used by our servers.
        </p>
        <p>
          <strong className="font-semibold text-slate-900">How to stop it.</strong> An organisation
          administrator can disconnect the mailbox in {COMPANY.product} at any time, which deletes
          the stored token. Access can also be removed from the Microsoft or Google side: in your
          Microsoft account under “Apps and services” (or by your Microsoft 365 administrator), or in
          your Google Account under “Security” → “Third-party apps with account access”.
        </p>
        <p className="rounded-lg border border-sky-200 bg-sky-50 p-4 text-slate-800">
          {COMPANY.product}’s use and transfer to any other app of information received from Google
          APIs will adhere to the{' '}
          <a
            href="https://developers.google.com/terms/api-services-user-data-policy"
            className="font-medium text-[#0089c3] hover:underline"
            target="_blank"
            rel="noreferrer"
          >
            Google API Services User Data Policy
          </a>
          , including the Limited Use requirements.
        </p>
      </>
    ),
  },
  {
    id: 'how-we-use',
    heading: 'How we use information',
    body: (
      <Bullets
        items={[
          'To provide the platform: running each app, signing people in, and sending the emails, SMS codes and notifications the service relies on.',
          'To keep it secure: authenticating users, detecting misuse, and keeping an audit trail of changes.',
          'To support customers: answering questions and investigating problems, including — when a customer asks us to — viewing their account as a Stratum support user.',
          'To run our business: billing, account administration, and meeting our legal obligations.',
          'To improve the service, using aggregated and technical information about how the platform performs.',
        ]}
      />
    ),
  },
  {
    id: 'legal-bases',
    heading: 'Legal bases',
    body: (
      <>
        <p>Where we are the controller, we rely on:</p>
        <Bullets
          items={[
            <Term key="k" label="Contract">
              to provide the service a customer has signed up for, and the accounts its users need.
            </Term>,
            <Term key="li" label="Legitimate interests">
              to keep the platform secure, support our customers and improve the service — balanced
              against your rights.
            </Term>,
            <Term key="lo" label="Legal obligation">
              for accounting, tax and other records the law requires us to keep.
            </Term>,
            <Term key="cn" label="Consent">
              where we ask for it, such as connecting an email account, which you can withdraw at
              any time.
            </Term>,
          ]}
        />
      </>
    ),
  },
  {
    id: 'sharing',
    heading: 'Who we share it with',
    body: (
      <>
        <p>
          We do not sell personal data. We share it only with the service providers that help us
          run {COMPANY.product}, under contracts that require them to protect it and use it only on
          our instructions:
        </p>
        <Bullets
          items={[
            <Term key="az" label="Microsoft Azure">
              hosting, databases, file storage, key management and application monitoring.
            </Term>,
            <Term key="a0" label="Auth0 (Okta)">sign-in and identity management.</Term>,
            <Term key="tw" label="Twilio and Twilio SendGrid">
              SMS sign-in codes and invitations, and sending email when an organisation has not
              connected its own mailbox.
            </Term>,
            <Term key="mp" label="Google Maps Platform and Azure Maps">
              address search, mapping, route distances and CO₂ estimates.
            </Term>,
            <Term key="ms" label="Microsoft and Google">
              when an organisation connects a mailbox, to send its emails as described in section 4.
            </Term>,
          ]}
        />
        <p>
          Where a customer connects a third-party service — such as a GPS or tachograph provider —
          we exchange data with that service on the customer’s instruction. We may also disclose
          information where the law requires it, or to protect the rights and safety of our users
          and others.
        </p>
      </>
    ),
  },
  {
    id: 'transfers',
    heading: 'Where data is stored',
    body: (
      <p>
        {COMPANY.product} is hosted in {COMPANY.hostingRegion}. Some of our service providers may
        process data outside the European Economic Area or the United Kingdom; where they do, we rely
        on an adequacy decision or the European Commission’s Standard Contractual Clauses, with
        additional safeguards where needed.
      </p>
    ),
  },
  {
    id: 'retention',
    heading: 'How long we keep it',
    body: (
      <>
        <p>
          Business records are kept for as long as the customer’s subscription is active, and are
          returned or deleted when it ends, except where the law requires us to keep them for longer
          (for example, invoices for tax purposes).
        </p>
        <p>
          A connected mailbox’s token is deleted as soon as the mailbox is disconnected. Technical
          logs are kept for a limited period for security and troubleshooting, then deleted.
        </p>
      </>
    ),
  },
  {
    id: 'security',
    heading: 'How we protect it',
    body: (
      <p>
        Data is encrypted in transit and at rest. Access is limited by role, each customer’s
        data is logically separated from every other customer’s, secrets are held in a managed key vault, and
        changes to important records are recorded in an audit trail. No system is perfectly secure,
        but we work to protect your information and will tell affected customers promptly about any
        breach that puts it at risk.
      </p>
    ),
  },
  {
    id: 'your-rights',
    heading: 'Your rights',
    body: (
      <>
        <p>
          Under data-protection law (including the GDPR and UK GDPR) you can ask to access, correct,
          delete or export your personal data, to restrict or object to how it is used, and to
          withdraw consent you have given.
        </p>
        <p>
          If your data is held in {COMPANY.product} by a business you work with, please contact that
          business first — it controls that data, and we will help it respond. Otherwise, write to
          us at <Email address={COMPANY.privacyEmail} />.
        </p>
        <p>
          You also have the right to complain to a supervisory authority — in Ireland, the Data
          Protection Commission (dataprotection.ie); in the UK, the Information Commissioner’s
          Office (ico.org.uk).
        </p>
      </>
    ),
  },
  {
    id: 'cookies',
    heading: 'Cookies and local storage',
    body: (
      <p>
        {COMPANY.product} uses only the cookies and browser storage it needs to work: keeping you
        signed in, remembering which account and organisation you are working in, and your screen
        preferences. We do not use advertising or cross-site tracking cookies.
      </p>
    ),
  },
  {
    id: 'children',
    heading: 'Children',
    body: (
      <p>
        {COMPANY.product} is a business service and is not intended for anyone under 18. We do not
        knowingly collect data from children.
      </p>
    ),
  },
  {
    id: 'changes',
    heading: 'Changes to this policy',
    body: (
      <p>
        We may update this policy as the platform changes. The date at the top shows when it last
        changed, and we will tell customers about significant changes before they take effect.
      </p>
    ),
  },
  {
    id: 'contact',
    heading: 'Contact us',
    body: (
      <p>
        Questions about this policy or your data: <Email address={COMPANY.privacyEmail} />, or write
        to {COMPANY.legalName}, {COMPANY.registeredAddress}. See also our{' '}
        <Link to="/terms" className="font-medium text-[#0089c3] hover:underline">
          terms of service
        </Link>
        .
      </p>
    ),
  },
];

export function PrivacyPage() {
  return (
    <LegalPage
      title="Privacy policy"
      intro={
        <p className="text-base">
          We built {COMPANY.product} to run transport businesses, not to collect data. This policy
          sets out, in plain terms, what we process and why — including exactly what happens when
          you connect a Microsoft 365 or Google mailbox.
        </p>
      }
      sections={SECTIONS}
    />
  );
}
